import { useQueryClient, type MutationFunction } from "@tanstack/react-query";
import type { Money } from "@/lib/domain/Money";
import type { TransactionAction } from "@/features/collections/TransactionAction";
import type { TransactionDetailsModel } from "@/features/collections/api/models/TransactionDetailsModel";
import { clearCacheAfterTransactionMutation } from "@/features/collections/api/clearCacheAfterTransactionMutation";
import { patchTransactionAfterMutation } from "@/features/collections/api/patchTransactionAfterMutation";
import { paginatedPrepend, useHTTP, useHTTPMutation, type HTTPResponse } from "@/lib/http";

export interface TransactionCreateRequestModel {
	readonly variantId: string;
	readonly occurredDate: string;
	readonly action: TransactionAction;
	readonly quantity: number;
	readonly unitAmount: Money;
}

export interface TransactionCreateRequestProperties {
	readonly requestModel: TransactionCreateRequestModel;
	readonly collectionId: string;
}

export const useTransactionCreate = () => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const mutationFn: MutationFunction<HTTPResponse<TransactionDetailsModel>, TransactionCreateRequestProperties> = requestProperties => {
		return http<TransactionDetailsModel, TransactionCreateRequestModel>({
			method: "POST",
			url: `/collection/${requestProperties.collectionId}/transaction`,
			body: requestProperties.requestModel
		});
	};

	return useHTTPMutation({
		mutationFn,
		onSuccess: (data, requestProperties) => {
			patchTransactionAfterMutation(queryClient, requestProperties.collectionId, data.body.variantId, paginatedPrepend(data.body));
			clearCacheAfterTransactionMutation(queryClient, requestProperties.collectionId, data.body.variantId);
		}
	});
};
